import { ChangeEventHandler, forwardRef, KeyboardEventHandler, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';

import { Pill } from '../Pill';
import { Typography } from '../Typography';

import styles from './Tags.module.scss';

export interface ITagsProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'onKeyDown'> {
    values?: string[];
    onValueChange?: (values: string[]) => void;
    error?: boolean;
}

export const Tags = forwardRef<HTMLInputElement, ITagsProps>(
    ({ values: valuesControlled, onValueChange, error, disabled, ...props }, ref) => {
        const [values, setValues] = useState<string[]>(valuesControlled || []);
        const [newValue, setNewValue] = useState('');

        const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
            setNewValue(e.target.value);
        };

        const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
            if (e.code === 'Enter') {
                handleAdd(newValue);
                e.preventDefault();
            }
        };

        const handleAdd = (value: string) => {
            if (!value) return;
            value = value.trim();

            const existing = values.find((x) => x === value);
            if (existing) {
                setNewValue('');
                return;
            }

            setValues([...values, value]);
            onValueChange?.([...values, value]);
            setNewValue('');
        };

        const handleDelete = (value: string) => () => {
            if (disabled) return;
            const newValues = values.filter((v) => v !== value);
            setValues(newValues);
            onValueChange?.(newValues);
        };

        useLayoutEffect(() => {
            if (valuesControlled) {
                setValues(valuesControlled);
            }
        }, [valuesControlled]);

        return (
            <>
                <div
                    aria-disabled={disabled}
                    className={classNames(styles.tagsWrapper, {
                        [styles.error]: !!error,
                        [styles.disabled]: !!disabled,
                    })}
                >
                    {values.map((label) => (
                        <Pill key={label} className={styles.tag} colorMode="hard" onClick={handleDelete(label)}>
                            <Typography color="inherit" style={{ whiteSpace: 'nowrap' }}>
                                {label}
                            </Typography>
                        </Pill>
                    ))}
                    <input
                        ref={ref}
                        value={newValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        className={styles.input}
                        disabled={disabled}
                        {...props}
                    />
                </div>
            </>
        );
    },
);
